/**
 * Single source of truth for the Service × City link graph.
 *
 * Lists the services that have a dedicated `/<service>/in/$city` route
 * template, paired with the priority cities (CITY_HUBS) the graph fans out to.
 *
 * Used by:
 *   - Homepage hub block (depth-1 crawl path)
 *   - SiteFooter expanded service-area block
 *   - CityHomePage "Services in {city}" cross-link
 *   - sitemap.xml entries
 */

import { CITY_HUBS, type CityHub } from "@/data/cityHubs";

export interface ServiceCityService {
  /** URL slug — must match the route file `<slug>.in.$city.tsx`. */
  slug: "awnings" | "boat-covers" | "pergola-covers" | "recover-awnings" | "case-studies";
  /** Full descriptive label used in anchor text. */
  label: string;
  /** Short label for compact UI (footer summary, etc.). */
  shortLabel: string;
}

export const SERVICE_CITY_SERVICES: ServiceCityService[] = [
  { slug: "awnings", label: "Custom Awnings", shortLabel: "Awnings" },
  { slug: "pergola-covers", label: "Patio & Pergola Covers", shortLabel: "Patio Covers" },
  { slug: "boat-covers", label: "Boat Covers & Marine Canvas", shortLabel: "Boat Covers" },
  { slug: "recover-awnings", label: "Awning & Canopy Recover", shortLabel: "Awning Recover" },
  { slug: "case-studies", label: "Project Case Studies", shortLabel: "Case Studies" },
];

/** Priority cities — every CITY_HUB qualifies. */
export const PRIORITY_CITIES: CityHub[] = CITY_HUBS;

/** Full city list available for service-in-city pages. */
export const ALL_CITIES: CityHub[] = CITY_HUBS;

export function serviceCityHref(serviceSlug: ServiceCityService["slug"], citySlug: string): string {
  return `/${serviceSlug}/in/${citySlug}`;
}
